import React, { Suspense, lazy, useEffect, useContext, useState } from 'react';
import { Routes, Route, NavLink, useLocation } from 'react-router-dom';
import {
  RedirectToSignIn,
  SignIn,
  SignUp,
  UserButton,
  OrganizationList,
  OrganizationSwitcher,
  useOrganization,
  useOrganizationList,
  useAuth,
} from '@clerk/react';
import { clerkLight, clerkDark } from './shared/clerk-appearance';
import { useUIStore } from './stores/ui-store';
import { useCastStore, refreshImageCloudMap } from './stores/cast-store';
import { useFontStore } from './stores/font-store';
import { useFontLoader } from './shared/hooks/useFontLoader';
import { useSyncRefresh } from './shared/hooks/useSyncRefresh';
import { ToastContainer } from './shared/components/ToastContainer';
import { LoaderCenter } from './shared/components/Loader';
import { AuthBridge } from './shared/components/AuthBridge';
import { OrgSetup } from './features/auth/OrgSetup';
import { AuthLayout } from './features/auth/AuthLayout';
import { ClerkBypassCtx } from './features/auth/clerk-bypass';
import { DesktopSignIn } from './features/auth/DesktopSignIn';
import { DesktopAuthGate, DesktopBridge, DesktopOrgGuard } from './features/auth/desktop-auth';
import { ElectronOrganizationSwitcher } from './features/auth/ElectronOrganizationSwitcher';
import { ElectronUserButton } from './features/auth/ElectronUserButton';
import { UpdateBadge } from './shared/components/UpdateBadge';
import { PrivacyPolicy } from './features/legal/PrivacyPolicy';
import { DataDeletion } from './features/legal/DataDeletion';

const PresentationList = lazy(() =>
  import('./features/presentations/PresentationList').then((m) => ({ default: m.PresentationList })),
);
const PresentationEditor = lazy(() =>
  import('./features/presentations/PresentationEditor').then((m) => ({ default: m.PresentationEditor })),
);
const SlideCanvasEditor = lazy(() =>
  import('./features/presentations/SlideCanvasEditor').then((m) => ({ default: m.SlideCanvasEditor })),
);
const BibleBrowser = lazy(() =>
  import('./features/bible/BibleBrowser').then((m) => ({ default: m.BibleBrowser })),
);
const SongList = lazy(() =>
  import('./features/songs/SongList').then((m) => ({ default: m.SongList })),
);
const SongDetail = lazy(() =>
  import('./features/songs/SongDetail').then((m) => ({ default: m.SongDetail })),
);
const LiveController = lazy(() =>
  import('./features/live/LiveController').then((m) => ({ default: m.LiveController })),
);
const Settings = lazy(() =>
  import('./features/settings/Settings').then((m) => ({ default: m.Settings })),
);
// ── Nav items ──────────────────────────────────────────────────────────────
// adminOnly: only rendered when user has org:admin role (CLERK_ENABLED)
// clerkOnly: only rendered when CLERK_ENABLED
const navItems = [
   {
    path: '/live',
    label: 'En Vivo',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
        <circle cx="12" cy="12" r="2" /><path d="M8.93 8.93a5 5 0 1 0 6.14 0" /><path d="M5.07 5.07a9 9 0 1 0 13.86 0" />
      </svg>
    ),
  },
  {
    path: '/',
    label: 'Presentaciones',
    exact: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
        <path d="M3 12L12 3l9 9" /><path d="M9 21V12h6v9" /><path d="M3 12v9h18V12" />
      </svg>
    ),
  },
  {
    path: '/bible',
    label: 'Biblia',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
      </svg>
    ),
  },
  {
    path: '/songs',
    label: 'Canciones',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
        <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
      </svg>
    ),
  },
];

// ── App Shell (nav + content layout) ─────────────────────────────────────
interface NavItem { path: string; label: string; icon: React.ReactNode; exact?: boolean }

const settingsIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

function AppShell({ navItems, isFullscreen, isLiveView }: {
  navItems: NavItem[];
  isFullscreen: boolean;
  isLiveView: boolean;
}) {
  const { darkMode } = useUIStore();

  return (
    <div className="h-full flex w-full">
      {/* ── Sidebar desktop ──────────────────────────────────── */}
      <nav className="hidden sm:flex fixed left-0 top-0 bottom-0 w-20 bg-surface-card border-r border-stroke flex-col items-center py-5 gap-0.5 z-50">
        {SHOW_AUTH_UI ? (
          <div className="mb-4 flex items-center justify-center w-full px-2">
            {USE_DESKTOP_AUTH ? (
              <ElectronOrganizationSwitcher />
            ) : (
              <OrganizationSwitcher
                hidePersonal
                afterSelectOrganizationUrl="/"
                afterCreateOrganizationUrl="/"
                appearance={darkMode ? clerkDark : clerkLight}
              />
            )}
          </div>
        ) : (
          <div className="font-display font-bold text-xl text-accent mb-5 tracking-tight select-none">P</div>
        )}

        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.exact}
            className="relative flex flex-col items-center w-full px-2 py-1 gap-1 group"
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-7 bg-accent rounded-r-full" />
                )}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                  isActive ? 'bg-accent/10 text-accent' : 'text-ink-muted group-hover:bg-surface-hover group-hover:text-ink'
                }`}>
                  {item.icon}
                </div>
                <span className={`text-[10px] font-medium w-full text-center truncate leading-tight transition-colors ${
                  isActive ? 'text-accent' : 'text-ink-muted group-hover:text-ink-soft'
                }`}>
                  {item.label}
                </span>
              </>
            )}
          </NavLink>
        ))}

        {/* Bottom zone: UpdateBadge + Ajustes + UserButton */}
        <div className="mt-auto pb-2 flex flex-col items-center w-full gap-0.5">
          <UpdateBadge />
          {[
            { path: '/settings', label: 'Ajustes', icon: settingsIcon },
          ].map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className="relative flex flex-col items-center w-full px-2 py-1 gap-1 group"
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-7 bg-accent rounded-r-full" />
                  )}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                    isActive ? 'bg-accent/10 text-accent' : 'text-ink-muted group-hover:bg-surface-hover group-hover:text-ink'
                  }`}>
                    {item.icon}
                  </div>
                  <span className={`text-[10px] font-medium w-full text-center truncate leading-tight transition-colors ${
                    isActive ? 'text-accent' : 'text-ink-muted group-hover:text-ink-soft'
                  }`}>
                    {item.label}
                  </span>
                </>
              )}
            </NavLink>
          ))}
          {SHOW_AUTH_UI && (
            <div className="pt-1">
              {USE_DESKTOP_AUTH ? <ElectronUserButton /> : <UserButton />}
            </div>
          )}
        </div>
      </nav>

      {/* ── Content ──────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col h-full overflow-hidden sm:pl-20">
        <main className={`flex-1 ${isLiveView ? 'overflow-hidden' : 'overflow-y-auto'}`}>
          <Suspense fallback={<LoaderCenter />}>
            <Routes>
              <Route path="/" element={<PresentationList />} />
              <Route path="/presentations/:id" element={<PresentationEditor />} />
              <Route path="/presentations/:id/slide/:slideId" element={<SlideCanvasEditor />} />
              <Route path="/bible" element={<BibleBrowser />} />
              <Route path="/songs" element={<SongList />} />
              <Route path="/songs/:id" element={<SongDetail />} />
              <Route path="/live" element={<LiveController />} />
              <Route path="/settings" element={<Settings />} />
            </Routes>
          </Suspense>
        </main>

        {/* ── Bottom nav mobile ──────────────────────────────── */}
        {!isFullscreen && (
          <nav className="sm:hidden flex-none h-16 bg-surface-card border-t border-stroke flex justify-around items-center safe-bottom z-50">
            {([...navItems, { path: '/settings', label: 'Ajustes', icon: settingsIcon, exact: false }] as NavItem[]).map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center flex-1 h-full gap-1 text-[10px] font-medium transition-colors ${
                    isActive ? 'text-accent' : 'text-ink-muted'
                  }`
                }
              >
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        )}
      </div>
    </div>
  );
}

// ── Auth-aware hooks only valid inside ClerkProvider ──────────────────────
// If no PUBLISHABLE_KEY, Clerk is absent — these fall back gracefully.
const CLERK_ENABLED = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// True in the Electron renderer — drives the desktop deep-link auth flow.
const IS_ELECTRON = typeof window !== 'undefined' && window.electronAPI?.isElectron === true;

// Deep-link desktop auth only applies to the production Clerk instance (pk_live).
// The dev instance (pk_test) authenticates fine in-app on localhost, so dev:electron
// keeps the normal AuthGuard + <SignIn> flow.
const USE_DESKTOP_AUTH =
  IS_ELECTRON && import.meta.env.VITE_CLERK_PUBLISHABLE_KEY?.startsWith('pk_live') === true;

// LAN control mode: the device was launched from the operator's control QR, which
// embedded a host-issued token. REST authenticates via X-Desktop-Token (configured
// in main.tsx); the Clerk gate is skipped and AppShell hides the auth UI.
import { getLanToken } from './features/auth/lan-control';
const IS_LAN_CONTROL = !!getLanToken();
const SHOW_AUTH_UI = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY && !IS_LAN_CONTROL;

// Redirects to /sign-in when not authenticated; shows loader while Clerk resolves.
// In Electron, if Clerk hasn't loaded after 10 s (no internet or origin not allowed
// in Clerk Dashboard) we bypass the gate — Express handles localhost auth directly.
function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded } = useAuth();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (isLoaded || !window.electronAPI) return;
    const t = setTimeout(() => setTimedOut(true), 10_000);
    return () => clearTimeout(t);
  }, [isLoaded]);

  if (!isLoaded && !timedOut) return <LoaderCenter />;
  if (!isLoaded && timedOut) {
    return <ClerkBypassCtx.Provider value={true}>{children}</ClerkBypassCtx.Provider>;
  }
  if (!isSignedIn) return <RedirectToSignIn />;
  return <>{children}</>;
}

// Guards that app content only renders when user has an active org.
// Pending invitations → OrganizationList (accept flow); no org at all → OrgSetup (create flow).
// If user previously had an org but now has none → show removal notice.
function OrgGuard({ children }: { children: React.ReactNode }) {
  const clerkBypassed = useContext(ClerkBypassCtx);
  const { organization, isLoaded } = useOrganization();
  const { userId, signOut } = useAuth();
  const { userInvitations, isLoaded: listLoaded } = useOrganizationList({
    userInvitations: { infinite: true },
  });

  const hadOrgKey = userId ? `proclaim_had_org_${userId}` : null;

  useEffect(() => {
    if (organization && hadOrgKey) {
      localStorage.setItem(hadOrgKey, '1');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id, hadOrgKey]);

  // Electron offline fallback: Clerk timed out, server holds org via savedOrgId.
  if (clerkBypassed) return <>{children}</>;

  if (!isLoaded || !listLoaded) return <LoaderCenter />;

  if (organization) return <>{children}</>;

  const hadOrg = hadOrgKey ? localStorage.getItem(hadOrgKey) === '1' : false;
  const hasPendingInvites = (userInvitations?.count ?? 0) > 0;

  if (hasPendingInvites) {
    return (
      <AuthLayout>
        <OrganizationList
          hidePersonal
          afterSelectOrganizationUrl="/"
          afterCreateOrganizationUrl="/"
        />
      </AuthLayout>
    );
  }

  if (hadOrg) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center gap-4 text-center px-2">
          <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-red-500">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="18" y1="8" x2="23" y2="13" /><line x1="23" y1="8" x2="18" y2="13" />
            </svg>
          </div>
          <div>
            <h2 className="font-display font-bold text-lg text-ink">Sin acceso a la organización</h2>
            <p className="text-sm text-ink-muted mt-1 max-w-xs">
              Tu acceso a esta organización fue revocado. Contacta al administrador si crees que es un error.
            </p>
          </div>
          <button
            onClick={() => { if (hadOrgKey) localStorage.removeItem(hadOrgKey); signOut(); }}
            className="mt-2 px-4 py-2 rounded-lg bg-surface-hover text-ink text-sm font-medium hover:bg-surface-card transition-colors"
          >
            Cerrar sesión
          </button>
        </div>
      </AuthLayout>
    );
  }

  return <OrgSetup />;
}

// ── App ────────────────────────────────────────────────────────────────────
export function App() {
  const { darkMode } = useUIStore();
  const { init: initCast } = useCastStore();
  const { customFonts, fetchFonts } = useFontStore();
  const location = useLocation();

  useFontLoader(customFonts);
  useSyncRefresh();

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  useEffect(() => {
    initCast();
    // VPS web mode: token not ready at mount — 401 + spurious toast if called here.
    // Electron local server bypasses auth, so safe to call early there.
    // useSyncRefresh handles fetchFonts in VPS web mode via proclaim:sync after SSE connects.
    if (!CLERK_ENABLED || window.electronAPI || IS_LAN_CONTROL) {
      fetchFonts();
      refreshImageCloudMap().catch(() => {/* non-fatal */});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Routes that show full-screen content (hide shell nav)
  const isEditorView =
    location.pathname.startsWith('/presentations/') ||
    location.pathname.startsWith('/songs/') ||
    location.pathname.includes('/slide/');
  const isLiveView = location.pathname === '/live';
  const isFullscreen = isEditorView || isLiveView;

  // ── Auth shell (sign-in / sign-up pages outside the main layout) ──────
  // LAN control mode bypasses the auth shell entirely — the device is authorised
  // via the host-issued token (X-Desktop-Token), no Clerk sign-in.
  if (CLERK_ENABLED && !IS_LAN_CONTROL) {
    return (
      <>
        <ToastContainer />
        <Routes>
          {/* Branded auth pages. ?desktop=1 (opened from the Electron app in the
              system browser) runs the handoff-code flow after sign-in. */}
          <Route
            path="/sign-in/*"
            element={
              new URLSearchParams(window.location.search).get('desktop') === '1' ? (
                <DesktopSignIn />
              ) : (
                <AuthLayout>
                  <SignIn routing="path" path="/sign-in" fallbackRedirectUrl="/" />
                </AuthLayout>
              )
            }
          />
          <Route
            path="/sign-up/*"
            element={
              <AuthLayout>
                <SignUp routing="path" path="/sign-up" fallbackRedirectUrl="/" />
              </AuthLayout>
            }
          />

          {/* Public legal pages */}
          <Route path="/privacidad" element={<PrivacyPolicy />} />
          <Route path="/datos" element={<DataDeletion />} />

          {/* All other routes require auth + active org. Electron uses the desktop
              session gate (system-browser sign-in + handoff code); web uses AuthGuard. */}
          <Route
            path="/*"
            element={
              USE_DESKTOP_AUTH ? (
                <DesktopAuthGate>
                  <DesktopBridge />
                  <DesktopOrgGuard>
                    <AppShell
                      navItems={navItems}
                      isFullscreen={isFullscreen}
                      isLiveView={isLiveView}
                    />
                  </DesktopOrgGuard>
                </DesktopAuthGate>
              ) : (
                <AuthGuard>
                  <AuthBridge />
                  <OrgGuard>
                    <AppShell
                      navItems={navItems}
                      isFullscreen={isFullscreen}
                      isLiveView={isLiveView}
                    />
                  </OrgGuard>
                </AuthGuard>
              )
            }
          />
        </Routes>
      </>
    );
  }

  // ── Local / Electron mode (no Clerk) ────────────────────────────────────
  return (
    <div className="h-full flex">
      <ToastContainer />
      <AppShell navItems={navItems} isFullscreen={isFullscreen} isLiveView={isLiveView} />
    </div>
  );
}
